<script lang="ts" setup>
import { computed, ref, watch } from "vue";
import OmniSelect from "@/components/selects/OmniSelect.vue";
import PipAttachProvider from "@/components/layout/PipAttachProvider.vue";
import { useValidation } from "@/composables/useValidation";
import type { TimePickerProps } from "@/types/props";
import { timeMask } from "@/constants/mask";

defineOptions({ inheritAttrs: false });

const model = defineModel<string | null>();

const props = withDefaults(defineProps<TimePickerProps>(), {
  minuteStep: 5,
  required: false,
  disabled: false,
  rules: () => [],
});

const emit = defineEmits<{
  (e: "search", value: string | null | undefined): void;
}>();

const DEFAULT_ZERO_VALUE = "00";

const { requiredValidator } = useValidation();

const inputValue = ref<string | null>("");
const minutes = ref<string[]>([]);
const active = ref<boolean>(false);
const errorMessage = ref<string>("");
const isError = ref<boolean>(false);

const hours = generateNumbStringArray(24);

const splitValue = computed(() => (model.value ? model.value.split(":") : []));

const hourValue = computed(() => splitValue.value[0] || DEFAULT_ZERO_VALUE);

const minuteValue = computed(() => splitValue.value[1] || DEFAULT_ZERO_VALUE);

const preparedRules = computed(() => {
  const rules = [timeFormatValidator];

  if (props.required) {
    rules.push(requiredValidator);
  }

  return [...rules, ...props.rules];
});

const isComplete = computed(() => /^[0-2][0-9]:[0-5][0-9]$/.test(inputValue.value ?? ""));

watch(model, () => {
  inputValue.value = model.value ?? null;
  initMinutes();
});

watch(inputValue, (value) => {
  if (value === null) {
    model.value = value;

    return;
  }

  if (!validateTime(value)) {
    errorMessage.value = "Invalid time format";
    isError.value = true;

    return;
  }

  if (isComplete.value) {
    errorMessage.value = "";
    isError.value = false;
    model.value = value;
  }
});

inputValue.value = model.value || "";
initMinutes();

function search() {
  emit("search", model.value);
}

function initMinutes() {
  minutes.value = generateNumbStringArray(59, props.minuteStep);

  if (minutes.value.indexOf(minuteValue.value) === -1) {
    minutes.value.push(minuteValue.value);
    minutes.value.sort();
  }
}

function generateNumbStringArray(length: number, step = 1): string[] {
  return generateNumberStringSteppedArray(0, length, step);
}

function generateNumberStringSteppedArray(start: number, stop: number, step: number): string[] {
  return Array.from({ length: (stop - start) / step + 1 }, (_, i) => (start + i * step).toString().padStart(2, "0"));
}

function valueTemplate(hour: string, minute: string): string {
  return `${hour}:${minute}`;
}

function onChangeHour(value: string) {
  model.value = valueTemplate(value, minuteValue.value);
}

function onChangeMinute(value: string) {
  model.value = valueTemplate(hourValue.value, value);
  active.value = false;
}

function timeRegex(value: string): RegExp[] {
  const hours = [/[0-2]/, value.charAt(0) === "2" ? /[0-3]/ : /[0-9]/];
  const minutes = [/[0-5]/, /[0-9]/];

  return value.length > 2 ? [...hours, /:/, ...minutes] : hours;
}

function timeFormatValidator(v: string): boolean | string {
  if (!v) return true;

  return /^[0-2][0-9]:[0-5][0-9]$/.test(v) || "Wrong time format";
}

function validateTime(value: string): boolean {
  if (value.length > 5) {
    return false;
  }

  const mask = timeRegex(value);

  for (let i = 0; i < value.length; i++) {
    if (!mask[i].test(value[i])) {
      return false;
    }
  }

  return true;
}
</script>

<template>
  <v-menu v-model="active" :close-on-content-click="false" :disabled="disabled" location="bottom right" offset="10">
    <template #activator="{ props: menuProps }">
      <v-text-field
        :id="id"
        v-model="inputValue"
        v-maska="timeMask"
        :class="{ required: required }"
        :disabled="disabled"
        :error="isError"
        :error-messages="errorMessage"
        :label="label"
        :rules="preparedRules"
        autocomplete="off"
        class="_v-digital-time-picker"
        color="primary"
        density="compact"
        hide-details="auto"
        placeholder="00:00"
        v-bind="$attrs"
        variant="outlined"
        @keyup.enter="search"
      >
        <template #append-inner>
          <v-icon v-bind="menuProps">mdi-clock-outline</v-icon>
        </template>
      </v-text-field>
    </template>
    <pip-attach-provider>
      <v-card class="d-flex align-center">
        <omni-select
          :items="hours"
          :model-value="hourValue"
          autocomplete="off"
          class="_v-digital-time-picker__select"
          color="primary"
          hide-details
          rounded
          variant="plain"
          @update:model-value="onChangeHour"
        />
        <span class="_v-digital-time-picker__comma"> : </span>
        <omni-select
          :items="minutes"
          :model-value="minuteValue"
          autocomplete="off"
          class="_v-digital-time-picker__select"
          color="primary"
          hide-details
          rounded
          variant="plain"
          @update:model-value="onChangeMinute"
        />
      </v-card>
    </pip-attach-provider>
  </v-menu>
</template>

<style lang="scss" scoped>
$font-size: 20px !default;
._v-digital-time-picker {
  &__select {
    height: 60px;
    width: 100px;

    :deep(.v-input__control) {
      .v-field__append-inner,
      .v-label {
        display: none;
      }

      .v-field__input {
        padding-top: 16px;
        justify-content: center;

        input {
          text-align: center;
          font-size: 20px;
        }

        .v-select__selection {
          font-size: 20px;
        }
      }
    }
  }

  &__comma {
    font-size: $font-size;
    margin: 0 auto;
  }
}
</style>
